import { modelFieldToDtoKey } from '@/core/models/organization-model-slug';
import {
  NormalizedDataStructureOptionsDto,
  associationFieldLeaf,
  normalizeDataStructureFieldName,
} from '@/organization/resources/data-structures/data-structure-options/data-structure-options.util';
import { formatDynamicListSelection } from '@/shared/components/dynamic-fields/dynamic-list-value.util';
import { DataStructureOptionsDto } from '@/shared/openapi';
import { unwrapCounterFieldValue } from '@/shared/utils/counter-format.util';
import { formatDateFromTimestamp } from '@/shared/utils/date.utils';

/** Between related entities (Alex vs Raul). */
export const DSO_TO_MANY_ENTITY_DELIMITER = ', ';

/** Inside one entity’s multi-value field (Supplier vs Manufacturer). */
export const DSO_TO_MANY_INTRA_ENTITY_DELIMITER = ' / ';

export function readRelatedField(
  entity: Record<string, unknown>,
  fieldName: string
): unknown {
  const trimmed = fieldName.trim();
  const dot = trimmed.indexOf('.');
  if (dot !== -1) {
    const head = trimmed.slice(0, dot);
    const rest = trimmed.slice(dot + 1);
    const nested = readRelatedField(entity, head);
    if (!nested || typeof nested !== 'object' || Array.isArray(nested)) {
      return undefined;
    }
    return readRelatedField(nested as Record<string, unknown>, rest);
  }

  const snake = modelFieldToDtoKey(trimmed);
  const camel = normalizeDataStructureFieldName(trimmed);
  if (Object.prototype.hasOwnProperty.call(entity, trimmed)) {
    return entity[trimmed];
  }
  if (Object.prototype.hasOwnProperty.call(entity, snake)) {
    return entity[snake];
  }
  if (Object.prototype.hasOwnProperty.call(entity, camel)) {
    return entity[camel];
  }
  return entity[trimmed] ?? entity[snake] ?? entity[camel];
}

function isBooleanField(
  fieldName: string,
  fieldType?: DataStructureOptionsDto.FieldTypeEnum | null
): boolean {
  if (fieldType === DataStructureOptionsDto.FieldTypeEnum.Boolean) return true;
  return /^(is|has)[A-Z_]/.test(fieldName) || /^(is_|has_)/.test(fieldName);
}

function resolveDateKind(
  fieldName: string,
  fieldType?: DataStructureOptionsDto.FieldTypeEnum | null
): 'date' | 'datetime' | null {
  if (fieldType === DataStructureOptionsDto.FieldTypeEnum.Datetime)
    return 'date';
  if (/Date$/.test(fieldName) || /_date$/.test(fieldName)) return 'date';
  if (/At$/.test(fieldName) || /_at$/.test(fieldName)) return 'datetime';
  return null;
}

export function formatDsoAssociationFieldValue(
  value: unknown,
  option: NormalizedDataStructureOptionsDto,
  translate: (key: string) => string,
  lang?: string | null
): string {
  const fieldName =
    option.association_field?.trim() || option.field_name?.trim() || '';
  const leaf = associationFieldLeaf(fieldName) || fieldName;
  return formatScalarEntityValue(value, option, leaf, translate, lang);
}

function formatScalarEntityValue(
  value: unknown,
  option: NormalizedDataStructureOptionsDto,
  fieldName: string,
  translate: (key: string) => string,
  lang?: string | null
): string {
  if (value === null || value === undefined || value === '') return '';

  if (
    typeof value === 'boolean' ||
    isBooleanField(fieldName, option.field_type)
  ) {
    if (typeof value === 'boolean') {
      return translate(value ? 'core.buttons.yes' : 'core.buttons.no');
    }
  }

  const dateKind = resolveDateKind(fieldName, option.field_type);
  if (dateKind && (typeof value === 'number' || typeof value === 'string')) {
    return (
      formatDateFromTimestamp(value, null, null, dateKind === 'datetime') ||
      String(value)
    );
  }

  const counter = unwrapCounterFieldValue(value);
  if (counter != null && typeof value === 'object' && !Array.isArray(value)) {
    return counter;
  }

  if (option.dynamic_field?.list_items?.length) {
    return formatDynamicListSelection(
      value,
      option.dynamic_field.list_items,
      lang,
      DSO_TO_MANY_INTRA_ENTITY_DELIMITER
    );
  }

  if (Array.isArray(value)) {
    return value
      .map(item =>
        formatScalarEntityValue(item, option, fieldName, translate, lang)
      )
      .filter(part => part !== '')
      .join(DSO_TO_MANY_INTRA_ENTITY_DELIMITER);
  }

  if (typeof value === 'object') {
    return '';
  }

  return String(value);
}

function readPathKeys(value: unknown, keys: string[]): unknown {
  if (!keys.length) return value;
  if (Array.isArray(value)) {
    return value.map(item => readPathKeys(item, keys));
  }
  if (value == null || typeof value !== 'object') return undefined;
  const next = readRelatedField(value as Record<string, unknown>, keys[0]);
  return readPathKeys(next, keys.slice(1));
}

function displayLeaf(item: unknown): string {
  if (item == null || item === '') return '';
  if (typeof item === 'object') return '';
  return String(item);
}

/**
 * Reads a dotted column path. When a segment is a collection and keys remain,
 * the rest of the path is read on each element (`shipping_order_products.product.name`).
 * A value that is itself an array (`dynamic_fields.color`) is left intact.
 */
export function readColumnPath(
  source: unknown,
  dataPath: string
): { value: unknown; perEntity: boolean } {
  const keys = dataPath.split('.').filter(key => key.length > 0);
  if (!keys.length) return { value: undefined, perEntity: false };
  const head = readPathKeys(source, [keys[0]]);
  return {
    value: readPathKeys(source, keys),
    perEntity: keys.length > 1 && Array.isArray(head),
  };
}

/** Formats one leaf, or joins one leaf per related entity. */
export function formatResolvedColumnValue(
  resolved: { value: unknown; perEntity: boolean },
  valueFormatter?: (value: unknown) => string
): unknown {
  if (resolved.perEntity && Array.isArray(resolved.value)) {
    return resolved.value
      .map(item => (valueFormatter ? valueFormatter(item) : displayLeaf(item)))
      .join(DSO_TO_MANY_ENTITY_DELIMITER);
  }
  return valueFormatter ? valueFormatter(resolved.value) : resolved.value;
}

/**
 * One formatted segment per related entity (same order as the collection).
 * Empty entities still occupy a slot so Name and Type stay aligned.
 */
export function formatToManyAssociationEntityValues(
  related: unknown,
  option: NormalizedDataStructureOptionsDto,
  translate: (key: string) => string,
  lang?: string | null
): string[] {
  const fieldName = option.association_field?.trim();
  if (!fieldName) return [];
  const leaf = associationFieldLeaf(fieldName) || fieldName;

  const entities = Array.isArray(related)
    ? related
    : related && typeof related === 'object'
      ? [related]
      : [];

  return entities.map(item => {
    if (!item || typeof item !== 'object') return '';
    const value = readRelatedField(item as Record<string, unknown>, fieldName);
    return formatScalarEntityValue(value, option, leaf, translate, lang);
  });
}

/**
 * Joins a to-many collection into a single cell, keeping entity order
 * aligned across columns (`Alex, Raul` vs `Supplier, Supplier`).
 */
export function formatToManyAssociationCell(
  related: unknown,
  option: NormalizedDataStructureOptionsDto,
  translate: (key: string) => string,
  lang?: string | null
): string {
  return formatToManyAssociationEntityValues(
    related,
    option,
    translate,
    lang
  ).join(DSO_TO_MANY_ENTITY_DELIMITER);
}
